import React, { useState, useMemo, useEffect } from "react";
import FastAPIClient from "../../../src/client";
import config from "../../../src/config";
import Skeleton from "@mui/material/Skeleton";
import Box from "@mui/material/Box";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { HiOutlinePencil } from "react-icons/hi2";

const client = new FastAPIClient(config);

interface Note {
    note_id: string;
    created_at: string;
    content: string;
    user: {
        first_name: string;
        last_name: string;
    };
}

interface NotesProps {
    clientId: string;
    userId: string;
}

const formatDate = (timestamp: string): string => {
    const date = new Date(timestamp);
    const day = String(date.getDate()).padStart(2, "0");
    const month = new Intl.DateTimeFormat("fr-FR", { month: "long" })
        .format(date)
        .replace(/^./, (match) => match.toUpperCase()); // Capitalize the first letter
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");

    return `${day} ${month} ${year} à ${hours}:${minutes}`;
};

const Notes: React.FC<NotesProps> = ({ clientId, userId }) => {
    const [notes, setNotes] = useState<Note[]>([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [newNote, setNewNote] = useState("");
    const [isLoading, setIsLoading] = useState(true);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [selectedNote, setSelectedNote] = useState<Note | null>(null);

    // Fetch notes from API
    useEffect(() => {
        const fetchNotes = async () => {
            try {
                const response = await client.fetchNotes(clientId);
                setNotes(response);
            } catch (error) {
                console.error("Error fetching notes:", error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchNotes();
    }, [clientId]);

    // Sort notes
    const sortedNotes = useMemo(() => {
        return [...notes]
            .sort(
                (a, b) =>
                    new Date(b.created_at).getTime() -
                    new Date(a.created_at).getTime()
            )
            .slice(0, 5); // Only keep the 5 most recent notes
    }, [notes]);

    // Group notes by month
    const groupNotesByMonth = (notes: Note[]) => {
        return notes.reduce((groups, note) => {
            let month = new Date(note.created_at).toLocaleString("fr-FR", {
                month: "long",
                year: "numeric",
            });

            month = month.replace(/^./, (match) => match.toUpperCase());

            if (!groups[month]) {
                groups[month] = [];
            }
            groups[month].push(note);
            return groups;
        }, {} as { [key: string]: Note[] });
    };

    const groupedNotes = groupNotesByMonth(sortedNotes);

    // State for popup message
    const [popupMessage, setPopupMessage] = useState<string | null>(null);
    const [popupType, setPopupType] = useState<"success" | "error" | null>(
        null
    );

    const handleSaveNote = async () => {
        if (!newNote.trim()) return;

        try {
            const noteData = {
                content: newNote,
                user_id: userId,
                client_id: clientId,
            };

            let response;
            if (selectedNote) {
                // If a note is selected, update it
                response = await client.updateNote(
                    selectedNote.note_id,
                    noteData
                );
                setNotes((prevNotes) =>
                    prevNotes.map((note) =>
                        note.note_id === selectedNote.note_id ? response : note
                    )
                );
            } else {
                // Otherwise, create a new note
                response = await client.createNote(noteData);
                setNotes((prevNotes) => [...prevNotes, response]);
            }

            setIsModalOpen(false);
            setNewNote("");
            setSelectedNote(null); // Clear selectedNote after saving note
            setPopupMessage("Les modifications ont été enregistrées.");
            setPopupType("success");

            setTimeout(() => setPopupMessage(null), 3000);
        } catch (error) {
            console.error("Error saving note:", error);
            setPopupMessage(
                "Une erreur est survenue lors de l'enregistrement."
            );
            setPopupType("error");
            setTimeout(() => setPopupMessage(null), 3000);
        }
    };
    const handleMenuClick = (
        event: React.MouseEvent<HTMLElement>,
        note: Note
    ) => {
        setAnchorEl(event.currentTarget);
        setSelectedNote(note);
    };

    const handleCloseMenu = () => {
        setAnchorEl(null);
    };

    const handleEditNote = () => {
        if (selectedNote) {
            // Open the modal with the content of the selected note to edit
            setNewNote(selectedNote.content);
            setIsModalOpen(true);
        }
        handleCloseMenu();
    };
    const handleDeleteNote = async () => {
        if (!selectedNote) return;

        try {
            await client.deleteNote(selectedNote.note_id);
            setNotes((prevNotes) =>
                prevNotes.filter(
                    (note) => note.note_id !== selectedNote.note_id
                )
            );
            setPopupMessage("Note supprimée avec succès.");
            setPopupType("success");
        } catch (error) {
            console.error("Error deleting note:", error);
            setPopupMessage("Erreur lors de la suppression.");
            setPopupType("error");
        } finally {
            handleCloseMenu();
            setSelectedNote(null); // Clear selectedNote state after deletion
            setTimeout(() => setPopupMessage(null), 3000);
        }
    };

    return (
        <div className="pt-10 w-full">
            {isLoading ? (
                <div className="mt-4">
                    <Box>
                        {[...Array(3)].map((_, index) => (
                            <Skeleton
                                key={index}
                                variant="rounded"
                                height={80}
                                className="mb-4"
                            />
                        ))}
                    </Box>
                </div>
            ) : (
                <>
                    {notes.length === 0 && (
                        <div
                            className="rounded-xl text-left p-4 text-white flex flex-row mb-5 mt-5 items-center"
                            style={{
                                background:
                                    "linear-gradient(to right, #ff6ec7, #d800ff, #ff7f00)",
                                WebkitBackgroundClip: "border-box",
                            }}
                        >
                            <div className="flex flex-col w-full md:text-base text-sm">
                                <strong>
                                    Aucune note pour l&apos;instant.
                                </strong>
                                Notez vos observations pour garder une trace des
                                échanges importants.
                            </div>
                            <button
                                className="px-2 py-1 bg-white text-black font-bold text-sm rounded-full"
                                onClick={() => setIsModalOpen(true)}
                            >
                                Commencer
                            </button>
                        </div>
                    )}
                    {notes.length > 0 && (
                        <div className="flex w-full justify-end">
                            <button
                                className="px-2 py-1 bg-gold text-black font-bold text-medium rounded-full"
                                onClick={() => setIsModalOpen(true)}
                            >
                                + Note
                            </button>
                        </div>
                    )}

                    {/* Notes Grouped by Month */}
                    {Object.keys(groupedNotes).map((month) => (
                        <div key={month}>
                            <h2 className="text-base mb-5 text-left">
                                {month}
                            </h2>
                            {groupedNotes[month].map((note) => (
                                <div
                                    key={note.note_id}
                                    className="mb-5 text-xs rounded-lg border border-gray-200 p-4"
                                >
                                    <div className="flex flex-col justify-between">
                                        <div className="flex flex-row justify-between items-center">
                                            <div className="flex w-1/2 text-base font-semibold">
                                                Note&nbsp;
                                                <span className="font-normal">
                                                    {" "}
                                                    {note.user && (
                                                        <>
                                                            par{" "}
                                                            {
                                                                note.user
                                                                    ?.first_name
                                                            }{" "}
                                                            {
                                                                note.user
                                                                    ?.last_name
                                                            }
                                                        </>
                                                    )}
                                                </span>
                                            </div>

                                            <div className="flex w-2/12 text-xs text-right">
                                                {formatDate(note.created_at)}
                                            </div>

                                            <button
                                                onClick={(e) =>
                                                    handleMenuClick(e, note)
                                                }
                                                className="w-1/12"
                                            >
                                                <HiOutlinePencil />
                                            </button>
                                        </div>

                                        <div className="mt-2 text-left text-base">
                                            {note.content}
                                        </div>

                                        {/* Three Dots Menu */}

                                        {/* Menu for Edit/Delete */}
                                        <Menu
                                            anchorEl={anchorEl}
                                            open={Boolean(anchorEl)}
                                            onClose={handleCloseMenu}
                                        >
                                            <MenuItem onClick={handleEditNote}>
                                                Editer
                                            </MenuItem>
                                            <MenuItem
                                                onClick={handleDeleteNote}
                                            >
                                                Supprimer
                                            </MenuItem>
                                        </Menu>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ))}
                </>
            )}

            {/* Modal for Adding a Note */}
            {isModalOpen && (
                <div className="fixed inset-0 flex items-end md:items-center justify-center bg-gray-800 bg-opacity-50 z-50">
                    <div className="bg-white p-6 rounded-xl w-full md:w-1/3 text-left">
                        <h2 className="text-lg font-bold mb-4">
                            Nouvelle note
                        </h2>
                        <textarea
                            className="w-full p-2 rounded"
                            rows={4}
                            value={newNote}
                            onChange={(e) => setNewNote(e.target.value)}
                            placeholder="Écrivez votre note ici..."
                        />
                        <div className="flex justify-end mt-4">
                            <button
                                className="mr-2 px-4 py-2 bg-gray-300 rounded-full"
                                onClick={() => setIsModalOpen(false)}
                            >
                                Annuler
                            </button>
                            <button
                                className="px-4 py-2 bg-gold text-black font-bold rounded-full"
                                onClick={handleSaveNote}
                            >
                                Enregistrer
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Display the popup message */}
            {popupMessage && (
                <div
                    className={`fixed bottom-4 left-1/2 transform -translate-x-1/2 p-4 rounded bg-opacity-80 text-white ${
                        popupType === "success" ? "bg-green-500" : "bg-red-500"
                    }`}
                >
                    {popupMessage}
                </div>
            )}
        </div>
    );
};

export default Notes;
